import { debounce, type DebouncedFunc } from 'lodash';
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type RefAttributes
} from 'react';

import { extractValue, persistEvent } from './utils';

export type Props = {
  /**
   * Debounce delay in milliseconds.
   * If 0, onChange fires immediately.
   */
  debounceMs?: number;
  /** Commit an empty value immediately, cancelling any pending change. */
  flushOnEmptyValue?: boolean;
  /**
   * Commit a still-pending change when the input unmounts, instead of dropping
   * it. Defaults to `true`, which is what a **form field** needs: the value the
   * user typed is the thing being collected, so unmounting inside the debounce
   * window (paging a table, hiding a column, a refetch landing mid-typing) must
   * not discard it — the field looked edited, and then the edit was never there.
   *
   * Pass `false` for an input that merely *drives* something (a search box, a
   * filter): once it is gone, acting on the last keystroke is at best useless,
   * and where the unmount is itself the reset it re-applies what the user just
   * abandoned.
   */
  flushOnUnmount?: boolean;
};

/**
 * Higher-Order Component that adds debounce functionality to any input-like component.
 *
 * The wrapped component will:
 * - Update its displayed value immediately while typing
 * - Debounce the parent-provided onChange callback
 * - Commit a pending change on blur, so leaving the field settles it at once
 * - Commit a pending change on unmount, unless `flushOnUnmount` is false
 * - Preserve all onChange arguments (supports multi-argument signatures like Select's (value, option))
 * - Remain compatible with form libraries (Ant Design Form, React Hook Form, etc.)
 *
 * @param WrappedComponent - Component that accepts value and onChange props
 * @returns A new component with debounceMs prop added
 *
 * @example
 * // Simple usage - types are inferred
 * const DebouncedInput = withDebouncedOnChange(Input);
 *
 * // With explicit ref type for better ref typing
 * const DebouncedInput = withDebouncedOnChange<InputRef>(Input);
 *
 * // Works with multi-argument onChange (e.g., Ant Design Select)
 * const DebouncedSelect = withDebouncedOnChange(Select);
 * // onChange will receive (value, option) as expected
 */
const withDebouncedOnChange = <R = unknown, P = Record<string, unknown>>(
  WrappedComponent: ComponentType<P>
): ForwardRefExoticComponent<PropsWithoutRef<P & Props> & RefAttributes<R>> => {
  type ChangeHandler = (...args: unknown[]) => void;

  const DebouncedComponent = forwardRef<R, P & Props>((props, ref) => {
    const {
      debounceMs = 300,
      flushOnUnmount = true,
      flushOnEmptyValue = false,
      ...restWithValueAndOnChange
    } = props;

    const { value, onChange, onBlur, ...rest } =
      restWithValueAndOnChange as P & {
        value?: unknown;
        onChange?: ChangeHandler;
        onBlur?: (...args: unknown[]) => void;
      };

    const [internalValue, setInternalValue] = useState<unknown>(value);

    // Pin onChange in a ref so the debounced function always invokes the
    // latest handler. Without this, every parent re-render that produces a new
    // onChange reference (e.g. Ant Design's Form.Item recreating its closure
    // on each render) would force us to recreate — and cancel — the pending
    // debounce, dropping in-flight keystrokes before the user pauses.
    //
    // Use useLayoutEffect so the ref is updated synchronously after commit,
    // before the browser fires the next input event — protecting against the
    // race where a fast keystroke arrives before a deferred passive effect
    // has had a chance to refresh the ref.
    const onChangeRef = useRef<ChangeHandler | undefined>(onChange);
    useLayoutEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const debouncedOnChange =
      useMemo<DebouncedFunc<ChangeHandler> | null>(() => {
        if (!debounceMs) return null;
        return debounce((...args: unknown[]) => {
          onChangeRef.current?.(...args);
        }, debounceMs);
      }, [debounceMs]);

    // Read at cleanup time so toggling the prop can never flush a change
    // mid-life; only the unmount path consults it.
    const flushOnUnmountRef = useRef(flushOnUnmount);
    flushOnUnmountRef.current = flushOnUnmount;

    useEffect(() => {
      return () => {
        if (!debouncedOnChange) return;

        if (flushOnUnmountRef.current) debouncedOnChange.flush();
        else debouncedOnChange.cancel();
      };
    }, [debouncedOnChange]);

    /**
     * Leaving the field ends the burst it was debouncing, so there is nothing
     * left to wait for: commit immediately rather than holding the value back
     * for the rest of the delay.
     *
     * Without this, anything that reads the value on the way out — a Save
     * button, a validation pass, a request built from the form — can observe
     * the previous value for up to `debounceMs` after the user has visibly
     * finished typing.
     */
    const handleBlur = (...args: unknown[]) => {
      debouncedOnChange?.flush();
      onBlur?.(...args);
    };

    const handleChange = (...args: unknown[]) => {
      // Extract value from first argument for internal display
      const firstArg = args[0];
      const newValue = extractValue(firstArg);
      setInternalValue(newValue);

      // Persist event if first arg is an event, otherwise pass as-is
      const persistedFirstArg = persistEvent(firstArg);
      const allArgs = [persistedFirstArg, ...args.slice(1)];

      if (debouncedOnChange) {
        if (flushOnEmptyValue && newValue === '') {
          debouncedOnChange.cancel();
          onChangeRef.current?.(...allArgs);
          return;
        }

        debouncedOnChange(...allArgs);
      } else {
        onChangeRef.current?.(...allArgs);
      }
    };

    const componentProps = {
      ...rest,
      ref,
      value: internalValue,
      onChange: handleChange,
      onBlur: handleBlur
    } as unknown as P & { ref: React.Ref<R> };

    return <WrappedComponent {...componentProps} />;
  });

  const wrappedDisplayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  DebouncedComponent.displayName = `withDebouncedOnChange(${wrappedDisplayName})`;

  return DebouncedComponent;
};

export default withDebouncedOnChange;
